import { PlayerId, HUMAN_PLAYER_ID } from './constants.js';
import { getUILoc, getCardName } from '../utils/cardUtils.js';

/**
 * Updates the game state if game over conditions are met.
 * This function mutates the draftState directly.
 */
export const _updateGameOverState = (draftState) => {
    if (draftState.game_over) return;

    const p1 = draftState.players[PlayerId.PLAYER1];
    const p2 = draftState.players[PlayerId.PLAYER2];

    let winner = null;

    if (p1.consciousness <= 0) {
        winner = PlayerId.PLAYER2;
    } else if (p2.consciousness <= 0) {
        winner = PlayerId.PLAYER1;
    }

    if (p1.deck.length === 0 || p2.deck.length === 0) {
        if (p1.consciousness > p2.consciousness) {
            winner = PlayerId.PLAYER1;
        } else if (p2.consciousness > p1.consciousness) {
            winner = PlayerId.PLAYER2;
        } else {
            winner = draftState.current_turn === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
        }
    }

    if (winner) {
        console.log(`[GAME_END_DEBUG] Winner determined: ${winner}. Updating state.`);
        draftState.game_over = true;
        draftState.winner = winner;
        console.log(`[GAME_END_DEBUG] draftState set to:`, { game_over: draftState.game_over, winner: draftState.winner });
        
        const isPlayerVictory = winner === HUMAN_PLAYER_ID;
        const winnerName = draftState.players[winner]?.name || 'Unknown';
        const loserName = draftState.players[winner === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1]?.name || 'Unknown';
        
        let message = '';
        if (p1.consciousness <= 0 || p2.consciousness <= 0) {
            message = getUILoc('MESSAGES.CONSCIOUSNESS_ZERO', { name: loserName });
        } else if (p1.deck.length === 0 || p2.deck.length === 0) {
            message = getUILoc('MESSAGES.DECK_OUT_CHECK');
        }

        // 追加演出：「対局終了時」の実装（勝敗演出の直前に挿し込む）
        let detailPattern = null;
        let matchEndArgs = {};

        if (draftState.surrendered_by) {
            detailPattern = 'SURRENDER';
            const surrenderedId = draftState.surrendered_by;
            const surrenderedObj = draftState.players[surrenderedId];
            const surrenderedDisplayName = surrenderedObj?.name || (surrenderedId === HUMAN_PLAYER_ID ? getUILoc('PLAYER_NAME.SELF') : getUILoc('PLAYER_NAME.OPPONENT'));

            const displayText = getUILoc('MESSAGES.MATCH_END_DETAIL_SURRENDER', { name: surrenderedDisplayName });

            matchEndArgs = {
                pattern: 'SURRENDER',
                player_name: surrenderedDisplayName,
                display_text: displayText
            };
        } else if (p1.consciousness <= 0 || p2.consciousness <= 0) {
            detailPattern = 'CONSCIOUSNESS_ZERO';
            const loserId = p1.consciousness <= 0 ? PlayerId.PLAYER1 : PlayerId.PLAYER2;
            const loserObj = draftState.players[loserId];
            const loserDisplayName = loserObj?.name || (loserId === HUMAN_PLAYER_ID ? getUILoc('PLAYER_NAME.SELF') : getUILoc('PLAYER_NAME.OPPONENT'));
            const currentConsciousness = loserObj?.consciousness ?? 0;
            
            // トドメとなったカード名の判定
            let causeCardName = getUILoc('CARD_TYPE.CARD') || 'カード';
            const lastSourceCard = draftState.temp_effect_data?.last_consciousness_source_card;
            if (lastSourceCard && lastSourceCard.target_player_id === loserId && lastSourceCard.card_name) {
                causeCardName = lastSourceCard.card_name;
            } else if (draftState.last_played_card) {
                const lpId = draftState.last_played_card.card_id || draftState.last_played_card.id;
                causeCardName = lpId ? getCardName(lpId) : (draftState.last_played_card.name || getUILoc('CARD_TYPE.CARD') || 'カード');
            }

            const displayText = getUILoc('MESSAGES.MATCH_END_DETAIL_CONSCIOUSNESS_ZERO', {
                cardName: causeCardName,
                name: loserDisplayName,
                consciousness: currentConsciousness
            });

            matchEndArgs = {
                pattern: 'CONSCIOUSNESS_ZERO',
                card_name: causeCardName,
                player_name: loserDisplayName,
                consciousness: currentConsciousness,
                display_text: displayText
            };
        } else if (p1.deck.length === 0 || p2.deck.length === 0) {
            detailPattern = 'DECK_OUT';
            const p1Name = p1.name || getUILoc('PLAYER_NAME.SELF');
            const p2Name = p2.name || getUILoc('PLAYER_NAME.OPPONENT');
            const p1Val = p1.consciousness;
            const p2Val = p2.consciousness;
            
            let op = '=';
            if (p1Val > p2Val) op = '>';
            else if (p1Val < p2Val) op = '<';

            const displayText = getUILoc('MESSAGES.MATCH_END_DETAIL_DECK_OUT', {
                p1Name,
                p1Val,
                op,
                p2Name,
                p2Val
            });

            matchEndArgs = {
                pattern: 'DECK_OUT',
                p1_name: p1Name,
                p1_consciousness: p1Val,
                operator: op,
                p2_name: p2Name,
                p2_consciousness: p2Val,
                display_text: displayText
            };
        }

        draftState.end_reason = detailPattern || (draftState.surrendered_by ? 'SURRENDER' : 'UNKNOWN');

        if (detailPattern) {
            const matchEndDetailEffect = {
                effect: {
                    effect_type: 'MATCH_END_DETAIL',
                    args: matchEndArgs
                },
                sourceCard: null
            };
            draftState.animation_queue.push(matchEndDetailEffect);
            console.log(`[GAME_END_DEBUG] Pushed MATCH_END_DETAIL to animation_queue.`, matchEndDetailEffect);
        }
        
        const gameResultEffect = {
            effect: {
                effect_type: 'GAME_RESULT',
                args: {
                    is_victory: isPlayerVictory,
                    message: message,
                    winner_name: winnerName
                }
            },
            sourceCard: null
        };
        draftState.animation_queue.push(gameResultEffect);
        console.log(`[GAME_END_DEBUG] Pushed GAME_RESULT to animation_queue.`, gameResultEffect);
        console.log(`[GAME_END_DEBUG] Animation queue length: ${draftState.animation_queue.length}`);
    }
};
